import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  Dropdown,
  Flex,
  Icon,
  M,
  Multiselect,
  TimePicker,
  TimeIntervals,
} from 'design-system-web';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { AdminUser } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { ALL_ISSUE_TYPES_VALUE } from './coverageHoursPanelUtils';
import { useCoverageHoursPanelOptions } from './useCoverageHoursPanelOptions';
import {
  DAY_OF_WEEK_ITEMS,
  DayItem,
  TeamCoverageEntry,
  formatDays,
  formatTimeDisplay,
} from './coverageHoursTeamUtils';
import styles from './CreateCoverageHoursPanel.scss';

type TeamCoverageCardProps = {
  entry: TeamCoverageEntry;
  onEdit: () => void;
};

function TeamCoverageCard({ entry, onEdit }: TeamCoverageCardProps) {
  const { t } = useTranslation();
  const schedule = `${formatDays(entry.daysOfWeek)}, ${formatTimeDisplay(entry.startTime)}-${formatTimeDisplay(entry.endTime)}`;

  return (
    <Flex direction="column" className={styles.teamCard}>
      <Flex justify="space-between" align="center">
        <M className={styles.teamCardName}>{entry.teamLabel}</M>
        <Button variant="text" onClick={onEdit}>
          {t('web.admin.serviceRequest.settings.coverageHours.create.edit')}
        </Button>
      </Flex>
      <M className={styles.teamCardSchedule}>{schedule}</M>
    </Flex>
  );
}

type AddTeamFormProps = {
  assignableTeams: AdminUser[];
  onDone: (entry: TeamCoverageEntry) => void;
  onDiscard: () => void;
};

function AddTeamForm({ assignableTeams, onDone, onDiscard }: AddTeamFormProps) {
  const { t } = useTranslation();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);

  const teamItems = useMemo(
    () => assignableTeams.map(team => ({ label: team.label, value: team.value })),
    [assignableTeams]
  );

  const selectedDayItems = DAY_OF_WEEK_ITEMS.filter(d =>
    daysOfWeek.includes(d.value)
  );

  const canSave = Boolean(
    teamId && daysOfWeek.length > 0 && startTime && endTime
  );

  const handleDone = () => {
    if (!canSave || !teamId || !startTime || !endTime) return;

    const teamLabel =
      teamItems.find(item => item.value === teamId)?.label ?? '';

    onDone({
      id: uuidv4(),
      teamId,
      teamLabel,
      daysOfWeek,
      startTime,
      endTime,
    });
  };

  return (
    <Flex direction="column" gap={4} className={styles.addTeamForm}>
      <div className={styles.coverageBlockGrid}>
        <Dropdown
          id="coverage-hours-team-select"
          testId="coverage-hours-team-dropdown"
          doTranslation={false}
          isFullWidth
          items={teamItems}
          value={teamId}
          onChange={item => setTeamId(String(item.value))}
          label={t(
            'web.admin.serviceRequest.settings.coverageHours.create.team'
          )}
          placeholder={t(
            'web.admin.serviceRequest.settings.coverageHours.create.teamPlaceholder'
          )}
          fixedLabel
          isRequired
          menuPosition="fixed"
        />
      </div>

      <div className={styles.coverageBlockGrid}>
        <Multiselect
          testId="coverage-hours-days-dropdown"
          doTranslation={false}
          items={DAY_OF_WEEK_ITEMS}
          value={selectedDayItems}
          onChange={(items: DayItem[]) =>
            setDaysOfWeek(items.map(i => i.value))
          }
          label={t(
            'web.admin.serviceRequest.settings.coverageHours.create.dayOfWeek'
          )}
          placeholder={t(
            'web.admin.serviceRequest.settings.coverageHours.create.dayOfWeekPlaceholder'
          )}
          fixedLabel
          menuPosition="fixed"
          truncateSelectedItems
        />
        <TimePicker
          testId="coverage-hours-start-time"
          label={t(
            'web.admin.serviceRequest.settings.coverageHours.create.startTime'
          )}
          value={startTime}
          onChange={setStartTime}
          unit={TimeIntervals.HALF_HOUR}
          showTimezone={false}
          showClear={false}
          fixedLabel
        />
        <TimePicker
          testId="coverage-hours-end-time"
          label={t(
            'web.admin.serviceRequest.settings.coverageHours.create.endTime'
          )}
          value={endTime}
          onChange={setEndTime}
          unit={TimeIntervals.HALF_HOUR}
          showTimezone={false}
          showClear={false}
          fixedLabel
        />
      </div>

      <Flex gap={3}>
        <Button
          variant="primary"
          size="large"
          testId="coverage-hours-team-done"
          disabled={!canSave}
          onClick={handleDone}
        >
          {t('web.admin.serviceRequest.settings.coverageHours.create.done')}
        </Button>
        <Button
          variant="secondary"
          size="large"
          testId="coverage-hours-team-discard"
          onClick={onDiscard}
        >
          {t(
            'web.admin.serviceRequest.settings.coverageHours.create.discard'
          )}
        </Button>
      </Flex>
    </Flex>
  );
}

export type CreateCoverageHoursProps = {
  categories: WorkOrderModuleCategoriesSettings[];
  categoryValue: string | null;
  onCategoryChange: (value: string) => void;
  issueTypeValue: string;
  onIssueTypeChange: (value: string) => void;
  assignableTeams: AdminUser[];
  savedTeams: TeamCoverageEntry[];
  onAddTeam: (entry: TeamCoverageEntry) => void;
};

export function CreateCoverageHours({
  categories,
  categoryValue,
  onCategoryChange,
  issueTypeValue,
  onIssueTypeChange,
  assignableTeams,
  savedTeams,
  onAddTeam,
}: CreateCoverageHoursProps) {
  const { t } = useTranslation();
  const { categoryItems, issueTypeItems } = useCoverageHoursPanelOptions(
    categories,
    categoryValue
  );
  const [isAddingTeam, setIsAddingTeam] = useState(false);

  const isAllIssueTypesSelected =
    Boolean(categoryValue) && issueTypeValue === ALL_ISSUE_TYPES_VALUE;

  return (
    <Flex direction="column" gap={4} className={styles.panelContent}>
      <div className={styles.categoryRow}>
        <Dropdown
          id="coverage-hours-category-select"
          testId="coverage-hours-category-dropdown"
          doTranslation={false}
          isFullWidth
          items={categoryItems}
          value={categoryValue}
          onChange={item => onCategoryChange(String(item.value))}
          label={t(
            'web.admin.serviceRequest.settings.coverageHours.create.category'
          )}
          placeholder={t(
            'web.admin.serviceRequest.settings.coverageHours.create.categoryPlaceholder'
          )}
          fixedLabel
          isRequired
          menuPosition="fixed"
        />

        {categoryValue && (
          <Dropdown
            id="coverage-hours-issue-type-select"
            testId="coverage-hours-issue-type-dropdown"
            doTranslation={false}
            isFullWidth
            items={issueTypeItems}
            value={issueTypeValue}
            onChange={item => onIssueTypeChange(String(item.value))}
            label={t(
              'web.admin.serviceRequest.settings.coverageHours.create.issueType'
            )}
            fixedLabel
            menuPosition="fixed"
          />
        )}
      </div>

      {categoryValue && (
        <>
          {isAllIssueTypesSelected && !isAddingTeam && savedTeams.length === 0 && (
            <Flex gap={2} align="flex-start" className={styles.infoMessage}>
              <Icon
                name="info-circle"
                set="FontAwesome"
                type="far"
                className={styles.infoIcon}
              />
              <M className={styles.infoText}>
                {t(
                  'web.admin.serviceRequest.settings.coverageHours.create.allIssueTypesInfo'
                )}
              </M>
            </Flex>
          )}

          {savedTeams.map(entry => (
            <TeamCoverageCard
              key={entry.id}
              entry={entry}
              onEdit={() => {}}
            />
          ))}

          {isAddingTeam && (
            <AddTeamForm
              assignableTeams={assignableTeams}
              onDone={entry => {
                onAddTeam(entry);
                setIsAddingTeam(false);
              }}
              onDiscard={() => setIsAddingTeam(false)}
            />
          )}

          {!isAddingTeam && (
            <Flex>
              <Button
                variant="secondary"
                size="large"
                testId="coverage-hours-add-team"
                onClick={() => setIsAddingTeam(true)}
              >
                {t(
                  'web.admin.serviceRequest.settings.coverageHours.create.addTeam'
                )}
              </Button>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
}
